import { useEffect, useImperativeHandle } from 'react';
import { PhoneNumberInput, Stack, Text } from '@adc/parallax-component-library';
import { useForm, useStore } from '@tanstack/react-form';

import type { PatientIntroFormProps } from '~/features/patientCreation/types';
import { patientIntroFormSchema } from '~/features/patientCreation/validations';
import { buildTestId } from '~/utils/buildTestId';

export const PatientIntroForm = ({
  ref,
  onSubmit,
  setIsNextDisabled,
  testID,
}: PatientIntroFormProps) => {
  const form = useForm({
    defaultValues: {
      phone: '',
    },
    validators: {
      onMount: patientIntroFormSchema,
      onChange: patientIntroFormSchema,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
  });

  const isNextDisabled = useStore(
    form.store,
    (state) => !state.canSubmit || state.isSubmitting,
  );

  useEffect(() => {
    setIsNextDisabled(isNextDisabled);
  }, [isNextDisabled, setIsNextDisabled]);

  useImperativeHandle(
    ref,
    () => ({
      submitForm: form.handleSubmit,
    }),
    [form.handleSubmit],
  );

  return (
    <Stack gap="$6" paddingTop="$2" testID={testID}>
      <Text
        type="$bodyBase.medium"
        color="$text.100"
        testID={buildTestId({ testID, suffix: 'title' })}
      >
        Patient Information
      </Text>

      <Stack flexDirection="row" gap="$6">
        <Stack flex={0.5} flexShrink={1} flexBasis="0%">
          <form.Field name="phone">
            {(field) => (
              <PhoneNumberInput
                testID={buildTestId({ testID, suffix: ['input', 'phone'] })}
                label="Phone Number"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                inputMode="tel"
                autoCapitalize="none"
                autoCorrect={false}
                error={field.state.meta.isTouched && !field.state.meta.isValid}
                supportingText={
                  field.state.meta.isTouched && !field.state.meta.isValid
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
              />
            )}
          </form.Field>
        </Stack>
      </Stack>
    </Stack>
  );
};
